import { find } from 'underscore';
import { observable, configure, action, observe, flow, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import termStore from "@uw-it-sis/lib-react/lib/TermStore";
import {getAggregateState} from "@uw-it-sis/lib-react/lib/StateUtils";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {isEmpty, isFalse} from "@uw-it-sis/lib-react/lib/Utils";

import registrationStore from "@uw-it-sis/lib-react-myplan/lib/RegistrationStore";

import api from '../../lib/Api';

class MajorsStore {
    state = States.initial;

    degrees = [];

    exceptionUtils = appStore.getExceptionUtils();
    analyticsStore = appStore.getAnalyticsStore();

    constructor() {
        makeObservable(this, {
            state: observable,
            mount: action
        });

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            registrationData: States.initial,
            degrees: States.initial,
            terms: States.initial,
        };
    }

    mount = () => {
        this.dataLoadStates = {
            registrationData: States.pending,
            degrees: States.pending,
            terms: States.pending,
        };

        this.registrationDataDisposer = observe(registrationStore, "state", function (change) {
            // Set the data state for this store to the new value.
            this.dataLoadStates.registrationData = change.newValue;

            // once reg data is either done or error, then determine if fetching degree programs is needed
            if (change.newValue === States.done || change.newValue === States.error) {
                let premajorIndicator = this.getMajorData().premajorIndicator ?? "X";
                // If premajorIndicator is "Y", then majors in reg data is "intended", declared degrees would come from user session data
                if (premajorIndicator === "Y" || change.newValue === States.error) {
                    // since user session degrees come as a code, fetch the degree programs list to be able to convert code to a degree name
                    this.loadDegrees();
                } else {
                    // since majors in reg data are declared, no need to fetch degree programs list
                    this.dataLoadStates.degrees = States.done;
                }
            }

            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        }.bind(this));

        this.termStoreDisposer = observe(termStore, "state", function (change) {
            // Set the data state for this store to the new value.
            this.dataLoadStates.terms = change.newValue;
            // once term data is fetched, and we have the current term, then fetch reg data using current term
            if (change.newValue === States.done) {
                let currentTerm = termStore.get('currentPlanableTerm');
                registrationStore.initialize(currentTerm.id);
            }
            // if fetching terms fails, set reg data state to error since we need a term to call for reg data
            // store will attempt to fetch degrees data and use user session data to provide the declared major
            if (change.newValue === States.error) {
                this.dataLoadStates.registrationData.error;
            }

            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        }.bind(this));

        // Load the currentTerm
        termStore.load('currentPlanableTerm');

        this.checkDataLoadStates();
    }

    loadDegrees = flow(function*() {
        this.analyticsStore.startTimer("Majors-LoadDegrees");
        if (appStore.isDevMode()) {
            console.log(`[${this.constructor.name}]: degrees data load started`);
        }
        try {
            this.degrees = yield api.getDegreePrograms();
            this.dataLoadStates.degrees = States.done;

            if (appStore.isDevMode()) {
                console.log(`[${this.constructor.name}]: degrees data load completed`);
            }
        } catch (e) {
            this.exceptionUtils.handleException(e, 'MajorsStore.loadDegrees', false);
            this.dataLoadStates.degrees = States.error;
        } finally {
            this.analyticsStore.endTimer("Majors-LoadDegrees");
            this.checkDataLoadStates();
        }
    }.bind(this));

    unmount = () => {
        this.termStoreDisposer();
        this.registrationDataDisposer();
    }

    getDegreeByCode = (code) => {
        return(
            find(
                this.degrees,
                (program) => {return program.auditCode === code;}
            )
        );
    }

    hasDegreesData = () => {
        return isFalse(isEmpty(this.degrees));
    }

    getMajorData = () => {
        const regData = registrationStore.getData();
        const { premajorIndicator, majors } = regData;
        return {
            premajorIndicator: premajorIndicator,
            majors: majors
        };
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates = () => {
        // if both reg data fetch and degrees fetch both fail, set store state to error since we don't have data
        if (this.dataLoadStates.registrationData === States.error && this.dataLoadStates.degrees === States.error) {
            this.state = States.error;
        } else {
            this.state = getAggregateState(this.dataLoadStates, true);
        }
    }
}

export default new MajorsStore();